/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'
import { usersApiService } from './users'
import { getUser } from '../auth'

const baseURL = '/api/v1/workflowtemplates'
const baseURL_v2 = '/api/v2/workflowtemplates'

interface TaskTeams {
  user?: { id: string }
  group?: { id: string }
}

interface MetadataTemplateType {
  id?: number
}
interface ActionDataType {
  metadataTemplateId: number
  value?: string | number | null
  metadataTemplate?: MetadataTemplateType
}
interface ActionsObjectType {
  '@type'?: string
  notifies?: { user?: { id: string } }[] | { group?: { id: string } }[]
  actionData?: ActionDataType[] | null
  actionType?: string
  copyToFsiId?: number
  copyToFsiName?: string
  usersGroups?: string
  selectedFolder: {
    id: number
  }
}

type MergedStepType = {
  systemHelperType?: string
  id?: string
}

interface StepType {
  nofAllValue?: string
  processingType?: string
  taskTeamStepLabel?: string | null
  taskTeams?: TaskTeams[]
  usersGroups?: string
  merged?: MergedStepType[]
}

interface GetAllForTable {
  id?: number[]
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  _noLoadingSpinner?: boolean
}

interface CreatedByUsertype {
  id?: number
  name?: string
}

interface PhaseType {
  type?: string
  steps?: StepType[]
  createRAActionType?: string
  approveActions?: ActionsObjectType[]
}

interface CreateUpdateWFTemplateRequestType {
  rejectionReviewers?: PhaseType
  trainees?: PhaseType
  approvers?: PhaseType
  reviewers?: PhaseType
  collaborators?: PhaseType
  initialApprovers?: PhaseType
  id?: number
  description?: string
  name?: string
  createdBy?: CreatedByUsertype
  createdAt?: number
}

interface TimeStampType {
  instantMatchType?: string
  instant?: number
}
interface ExportReportsFilterType {
  createdAt?: TimeStampType
  updatedAt?: TimeStampType
  createdByUserId?: number[]
  updatedByUserId?: []
}

interface MetaType {
  sortBy?: string
  sortDesc?: boolean
}

interface SaveAsWFTemplateType {
  description?: string
  name?: string
}

class ChangeRequestTypesApiService {
  async getAllForTable(params: GetAllForTable) {
    params = params ? params : { sortBy: 'NAME', sortDesc: false }
    return baseApiService.fetch(baseURL_v2, {
      method: 'GET',
      params: { ...params }
    })
  }

  async getById(typeId: number, useVersion1: boolean) {
    return baseApiService.fetch(useVersion1 ? baseURL + '/' + typeId : baseURL_v2 + '/' + typeId)
  }

  async getOptionsForAsyncPicker(search: string, pageStart: number) {
    const params = {
      name: search ? search : null,
      pageStart,
      pageSize: 25
    }

    const req = baseApiService.fetch(baseURL_v2 + '/picker', {
      method: 'GET',
      params
    })

    // REPLACE WITH FILTER SERCE AFTER C2R
    // return filterService.processAsyncPickerRequest(req, params);
    return req // REMOVEW AFTER REACT SERVICE WIRING
  }

  async get(id: number) {
    return baseApiService.fetch(baseURL + '/' + id)
  }

  // async create(data: CreateUpdateWFTemplateRequestType, transformedData: boolean, hideLoader: boolean) {
  async create(data: CreateUpdateWFTemplateRequestType) {
    return baseApiService.fetch(baseURL, {
      method: 'POST',
      // REVISIT: body: transformedData ? {...data} : this.assembleForServer(data)
      body: { ...data }
    })
  }

  // async update(id: number, data: CreateUpdateWFTemplateRequestType, transformedData: boolean, hideLoader: boolean) {
  async update(id: number, data: CreateUpdateWFTemplateRequestType) {
    // VERIFY THE USE OF id HERE
    return baseApiService.fetch(baseURL + '/' + id, {
      method: 'PUT',
      // REVISIT: body: transformedData ? {...data} : this.assembleForServer(data)
      body: { ...data }
    })
  }

  async exportReports(filter: ExportReportsFilterType, meta: MetaType) {
    const reqObj = { filter: filter, meta: meta }
    return baseApiService.fetch('api/reports/changerequesttypes/export', {
      method: 'POST',
      body: reqObj
    })
  }

  async saveAsCRType(fsiId: number, type: SaveAsWFTemplateType) {
    const typeLocal = { ...type, fsiId }
    return baseApiService.fetch(baseURL + '/createfromfsi', {
      method: 'POST',
      body: typeLocal
    })
  }

  // async remove(id: number, hideLoader: boolean) {
  async remove(id: number) {
    return baseApiService.fetch(baseURL + '/' + id, {
      method: 'DELETE'
    })
  }

  /**
   * Assembles workflow template data for UI display by merging user/group information
   * @param data - Raw workflow template data from API
   * @param addCurrentUser - Whether to automatically add current user to workflow steps
   * @returns Promise resolving to UI-formatted workflow template data
   */
  async assembleForUI(data: any, addCurrentUser?: boolean): Promise<any> {
    // Fetch dependencies internally, just like the legacy AngularJS implementation
    const usersGroupsData = await usersApiService.getAssembledUsersGroups()

    if (!usersGroupsData?.merged) {
      throw new Error('Failed to fetch users/groups data')
    }

    const result: any = {
      id: data.id,
      name: '',
      description: '',
      approvers: {},
      collaborators: {},
      initialApprovers: {},
      rejectionReviewers: {},
      reviewers: {},
      trainees: {}
    }

    const availableUsersGroups = usersGroupsData.merged
    const Constants = {
      user: { systemHelperType: { USER: 'USER', GROUP: 'GROUP' } },
      workflow: { processingType: { SERIAL: 'SERIAL', PARALLEL: 'PARALLEL', N_OF_ALL: 'N_OF_ALL' } }
    }

    if (!data.id) {
      result.approvers = { type: 'SETUP_WITH_WORKFLOW', steps: [], approveActions: [] }
      result.initialApprovers = { type: 'SETUP_WITH_WORKFLOW', steps: [] }
      result.collaborators = { type: 'SETUP_WITH_WORKFLOW', steps: [] }
      result.reviewers = { type: 'SETUP_WITH_WORKFLOW', steps: [] }
      result.rejectionReviewers = { type: 'USE_INITIATOR', steps: [], members: [] }
      result.trainees = { createRAActionType: 'FOR_NOBODY', steps: [], members: [] }
    } else {
      result.name = data.name
      result.description = data.description

      // Helper function to find user/group by id and type
      const findUserGroup = (id: string, systemHelperType: string) => {
        return availableUsersGroups.find((ug: any) => ug.id === id && ug.systemHelperType === systemHelperType)
      }

      // Initial approvers
      result.initialApprovers = {
        type: data.initialApprovers.type,
        steps: []
      }
      for (const step of data.initialApprovers.steps || []) {
        const approverObj: any = {
          processingType: step.processingType,
          taskTeamStepLabel: step.taskTeamStepLabel,
          nofAllValue: step.nofAllValue,
          taskTeams: step.taskTeams,
          merged: []
        }
        for (const taskTeam of step.taskTeams || []) {
          const id = taskTeam.group ? taskTeam.group.id : taskTeam.user.id
          const systemHelperType = taskTeam.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          approverObj.merged.push(findUserGroup(id, systemHelperType))
        }
        result.initialApprovers.steps.push(approverObj)
      }
      if (addCurrentUser && result.initialApprovers.type === 'SETUP_WITH_WORKFLOW') {
        const currentUser = getUser()
        if (currentUser) {
          for (const user of availableUsersGroups) {
            if (user.id === currentUser.id && user.systemHelperType === Constants.user.systemHelperType.USER) {
              result.initialApprovers.steps.push({
                processingType: Constants.workflow.processingType.SERIAL,
                nofAllValue: null,
                merged: [user]
              })
              console.log('Authorizer was added automatically')
            }
          }
        }
      }

      // Collaborators
      result.collaborators = {
        type: data.collaborators.type,
        steps: []
      }
      for (const step of data.collaborators.steps || []) {
        const approverObj: any = {
          processingType: step.processingType,
          nofAllValue: step.nofAllValue,
          taskTeamStepLabel: step.taskTeamStepLabel,
          taskTeams: step.taskTeams,
          merged: []
        }
        for (const taskTeam of step.taskTeams || []) {
          const id = taskTeam.group ? taskTeam.group.id : taskTeam.user.id
          const systemHelperType = taskTeam.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          approverObj.merged.push(findUserGroup(id, systemHelperType))
        }
        result.collaborators.steps.push(approverObj)
      }
      if (addCurrentUser && result.collaborators.type === 'SETUP_WITH_WORKFLOW') {
        const currentUser = getUser()
        if (currentUser) {
          for (const user of availableUsersGroups) {
            if (user.id === currentUser.id && user.systemHelperType === Constants.user.systemHelperType.USER) {
              result.collaborators.steps.push({
                processingType: Constants.workflow.processingType.SERIAL,
                nofAllValue: null,
                merged: [user]
              })
              console.log('Collaborator was added automatically')
            }
          }
        }
      }

      // Reviewers
      result.reviewers = {
        type: data.reviewers.type,
        steps: []
      }
      for (const step of data.reviewers.steps || []) {
        const approverObj: any = {
          processingType: step.processingType,
          nofAllValue: step.nofAllValue,
          taskTeamStepLabel: step.taskTeamStepLabel,
          taskTeams: step.taskTeams,
          merged: []
        }
        for (const taskTeam of step.taskTeams || []) {
          const id = taskTeam.group ? taskTeam.group.id : taskTeam.user.id
          const systemHelperType = taskTeam.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          approverObj.merged.push(findUserGroup(id, systemHelperType))
        }
        result.reviewers.steps.push(approverObj)
      }

      // Approvers
      result.approvers = {
        type: data.approvers.type,
        steps: [],
        approveActions: []
      }
      for (const step of data.approvers.steps || []) {
        const approverObj: any = {
          processingType: step.processingType,
          nofAllValue: step.nofAllValue,
          taskTeamStepLabel: step.taskTeamStepLabel,
          taskTeams: step.taskTeams,
          merged: []
        }
        for (const taskTeam of step.taskTeams || []) {
          const id = taskTeam.group ? taskTeam.group.id : taskTeam.user.id
          const systemHelperType = taskTeam.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          approverObj.merged.push(findUserGroup(id, systemHelperType))
        }
        result.approvers.steps.push(approverObj)
      }

      for (const actionSource of data.approvers.approveActions || []) {
        const action: any = {
          actionType: actionSource['@type'],
          actionData: actionSource['actionData'],
          notifies: actionSource['notifies'],
          merged: []
        }
        for (const notify of actionSource.notifies || []) {
          const id = notify.group ? notify.group.id : notify.user.id
          const systemHelperType = notify.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          action.merged.push(findUserGroup(id, systemHelperType))
        }
        if (action.actionType === 'MOVE') {
          action.selectedFolder = actionSource.moveToFsiId
        } else if (action.actionType === 'COPY') {
          action.selectedFolder = actionSource.copyToFsiId
        } else if (action.actionType === 'NOTIFY') {
          action.actionData = actionSource.customNotificationText
        }
        result.approvers.approveActions.push(action)
      }

      // Trainees
      result.trainees = {
        createRAActionType: 'FOR_NOBODY',
        steps: data.trainees.steps,
        members: []
      }
      if (data.trainees.steps?.[0]) {
        for (const taskTeam of data.trainees.steps[0].taskTeams || []) {
          const id = taskTeam.group ? taskTeam.group.id : taskTeam.user.id
          const systemHelperType = taskTeam.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          result.trainees.members.push(findUserGroup(id, systemHelperType))
        }
      }

      // Rejection reviewers
      result.rejectionReviewers = {
        type: data.rejectionReviewers.type,
        steps: data.rejectionReviewers.steps,
        members: []
      }
      if (data.rejectionReviewers.steps?.[0]) {
        for (const taskTeam of data.rejectionReviewers.steps[0].taskTeams || []) {
          const id = taskTeam.group ? taskTeam.group.id : taskTeam.user.id
          const systemHelperType = taskTeam.group
            ? Constants.user.systemHelperType.GROUP
            : Constants.user.systemHelperType.USER
          result.rejectionReviewers.members.push(findUserGroup(id, systemHelperType))
        }
      }
    }

    return result
  }
}

export const changeRequestTypesApiService = new ChangeRequestTypesApiService()
